import { useUser } from '@clerk/react-router';
import { useSelector } from '@xstate/react';
import { useEffect } from 'react';
import { UserContextV2 } from '~/app';
import { useLoadedProvider } from '~/features/vault/account/vaultSelectors';
import { identifyUser, resetObservabilityIdentity } from './identify';

export function useObservabilityIdentity(): void {
  const { user, isLoaded, isSignedIn } = useUser();
  const userRef = UserContextV2.useActorRef();
  const roleCode = useSelector(userRef, (s) => s.context.roleCode);
  const employerUserId = useSelector(userRef, (s) => s.context.employerUserId);
  const providerId = useSelector(userRef, (s) => s.context.providerId);
  const impersonatingProviderId = useSelector(userRef, (s) => s.context.impersonatingProviderId);
  const { provider } = useLoadedProvider();

  const clerkId = user?.id;
  const email = user?.primaryEmailAddress?.emailAddress;
  const clerkRole = (user?.publicMetadata as { role?: string } | undefined)?.role;
  const providerType = provider?.type?.providerTypeCode;
  // No dedicated tenant/org id in Context today — use the role-appropriate
  // persistent identifier so Dash0 can segment per-tenant.
  const tenant =
    employerUserId != null
      ? `employer:${employerUserId}`
      : providerId != null
        ? `provider:${providerId}`
        : undefined;

  useEffect(() => {
    if (!isLoaded) return;
    if (!isSignedIn) {
      resetObservabilityIdentity();
      return;
    }

    identifyUser({
      clerkId,
      email,
      clerkRole,
      appRole: roleCode,
      providerType,
      tenant,
      isImpersonating: impersonatingProviderId != null,
      impersonatingProviderId:
        impersonatingProviderId != null ? String(impersonatingProviderId) : undefined,
    });
  }, [
    isLoaded,
    isSignedIn,
    clerkId,
    email,
    clerkRole,
    roleCode,
    providerType,
    tenant,
    impersonatingProviderId,
  ]);
}

export const ObservabilityIdentityBridge = (): null => {
  useObservabilityIdentity();
  return null;
};
